import { useEffect, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Admin-only panel showing AWS infrastructure cost.
 * Two views:
 *   1. Infrastructure cost (total Launchpad spend, broken down by AWS service)
 *   2. Per-app cost (sub-apps with awsResourceTag mapped to their AWS resources)
 *
 * Both have a 24-48h data lag from AWS Cost Explorer — labeled in the UI.
 */
export default function AwsCostPanel() {
  const [view, setView] = useState('infra');
  const [infraData, setInfraData] = useState(null);
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const url = refresh ? '?refresh=1' : '';
      const [infra, byApp] = await Promise.all([
        api.get(`/api/aws/infrastructure-cost${url}`),
        api.get(`/api/aws/cost-by-app${url}`)
      ]);
      setInfraData(infra.data);
      setAppData(byApp.data);
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || e.message;
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => {
    setRefreshing(true);
    load(true);
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 mb-6">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base font-medium">AWS infrastructure cost</h2>
          <p className="text-xs text-muted">
            Cost Explorer data — {infraData?.asOf ? `as of ${new Date(infraData.asOf).toLocaleString()}` : '24-48h lag'}
            {infraData?.cached && <span className="ml-2 text-amber-700">(cached)</span>}
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="text-xs text-muted hover:text-ink px-3 py-1 border border-border rounded-lg hover:bg-neutral-50 disabled:opacity-50"
        >
          {refreshing ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      <div className="flex gap-1 mt-4 border-b border-border">
        <Tab active={view === 'infra'} onClick={() => setView('infra')}>By AWS service</Tab>
        <Tab active={view === 'apps'} onClick={() => setView('apps')}>By sub-app</Tab>
      </div>

      {loading && <p className="text-sm text-muted mt-4">Loading…</p>}

      {error && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <p className="font-medium mb-1">AWS data unavailable</p>
          <p>{error}</p>
          <p className="mt-2">
            Common causes: Cost allocation tag not yet activated (24h delay), IAM permission missing,
            or no data in the period. See deployment runbook.
          </p>
        </div>
      )}

      {!loading && !error && view === 'infra' && infraData && (
        <InfraView data={infraData} />
      )}

      {!loading && !error && view === 'apps' && appData && (
        <AppView data={appData} />
      )}
    </div>
  );
}

function InfraView({ data }) {
  if (!data.services || data.services.length === 0) {
    return (
      <div className="mt-4 text-sm text-muted">
        No AWS cost data available for the last {data.period}.
      </div>
    );
  }

  const max = Math.max(...data.services.map((s) => s.cost));

  return (
    <div className="mt-4">
      <p className="text-2xl font-medium mb-1">${data.total.toFixed(2)}</p>
      <p className="text-xs text-muted mb-4">Total over last {data.period}</p>

      <div className="space-y-2">
        {data.services.map((s) => (
          <div key={s.service} className="flex items-center gap-3 text-sm">
            <span className="w-40 truncate">{s.service}</span>
            <div className="flex-1 bg-neutral-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-ink rounded-full"
                style={{ width: `${(s.cost / max) * 100}%` }}
              />
            </div>
            <span className="w-20 text-right font-mono text-xs">${s.cost.toFixed(2)}</span>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted mt-4 italic">
        {data.note}
      </p>
    </div>
  );
}

function AppView({ data }) {
  const mapped = data.apps.filter((a) => a.awsResourceTag);
  const unmapped = data.apps.filter((a) => !a.awsResourceTag);

  return (
    <div className="mt-4">
      {mapped.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-2">AWS-hosted apps ({mapped.length})</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted">
              <tr>
                <th className="text-left py-2 font-medium">App</th>
                <th className="text-left py-2 font-medium">AWS tag</th>
                <th className="text-right py-2 font-medium">Cost ({data.period})</th>
              </tr>
            </thead>
            <tbody>
              {mapped.map((a) => (
                <tr key={a.appId} className="border-t border-border">
                  <td className="py-2">{a.appName}</td>
                  <td className="py-2 text-xs text-muted font-mono">{a.awsResourceTag}</td>
                  <td className="py-2 text-right font-mono">
                    {a.cost === null ? '—' : `$${a.cost.toFixed(2)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {unmapped.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-2 text-muted">Not AWS-hosted ({unmapped.length})</h3>
          <p className="text-xs text-muted mb-2">
            These apps don't run on our AWS infrastructure (external SaaS, browser-only, or no tag mapped).
          </p>
          <ul className="text-sm text-muted">
            {unmapped.map((a) => (
              <li key={a.appId} className="py-1">• {a.appName}</li>
            ))}
          </ul>
        </div>
      )}

      {data.orphanedTags && data.orphanedTags.length > 0 && (
        <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-medium text-amber-900 mb-1">
            Tagged AWS resources not mapped to any app
          </p>
          <p className="text-xs text-amber-800 mb-2">
            These tag values exist in AWS but no app in the launcher claims them. Either map them in Edit App, or update the tags.
          </p>
          <ul className="text-xs text-amber-900 font-mono">
            {data.orphanedTags.map((o) => (
              <li key={o.tag} className="py-0.5">{o.tag} — ${o.cost.toFixed(2)}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-muted border-t border-border pt-3 mt-4">
        <p className="font-medium mb-1">Untagged Launchpad infrastructure: ${data.untaggedCost.toFixed(2)}</p>
        <p>This is the launcher itself — ALB, RDS, ECS, etc. Not attributable to any single sub-app.</p>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm ${
        active ? 'border-b-2 border-ink font-medium -mb-px' : 'text-muted hover:text-ink'
      }`}
    >
      {children}
    </button>
  );
}
